import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Dialog, Input, NavBar, Popup, Skeleton, Toast} from 'antd-mobile';
import {getBottomNavBarContainerStyle, getNavBarContainerStyle} from "../../../utils/deviceUtils";
import EmptyState from "../../../components/EmptyState";
import {useDeliveryTaskOrderOperations, useDeliveryTaskOrders, useTempScanPhotos} from '../store/deliveryStore';
import {navigateBack} from "../../../components/Map/MapNavigationUtils";
import {t} from "../../../i18n";
import {openCamera, openNativeMapMode, openPhotoPicker, openTrackNumCamera} from "../../../utils/appRuntime";
import {DeliveryTaskOrderType} from "../graphql/types/DeliveryTaskOrderType";
import {DownOutline, UpOutline} from "antd-mobile-icons";
import {CloudUploadOutlined} from '@ant-design/icons';
import MultiPhotoSelectorView from '../components/MultiPhotoSelectorView';
import {PhotoType, PositionType} from '../services/PhotoService';
import multiPhotoService from '../services/MultiPhotoService';
import {NativePermissions} from '../components/PhotoNativeBridge';
import geolocationService, {GeoPosition} from "../services/GeolocationService";
import {useLocationStore} from "../../../store/locationStore";
import {OptimizedPhoto, useOptimizedPhotos} from '../hooks/useOptimizedPhotos';
import VirtualPhotoList from '../components/VirtualPhotoList';
import OptimizedPhotoViewer from '../components/OptimizedPhotoViewer';
import {ExceptionDictionaryType, ExceptionSubReasonType} from "../graphql/queries/getExceptionDictionaryForDelivery";
import {useNetwork} from 'hooks/useNetwork';
import VirtualOrderList from './components/VirtualOrderList';
// 新增：批量处理工具
import {OrderBatchProcessor, OrderProcessContext} from '../utils/OrderBatchProcessor';

const kMinSelectorPhotoLength = 2;
const kMaxSelectorPhotoLength = 5;

// 扫描结果类型
export interface ScannedItem {
    trackingNumber: string;
    stopNumber: string;
    frameDataUrl?: string;
    deliveryTaskOrderId?: number;
}

// 订单分组类型
interface OrderGroup {
    fullName: string;
    phone: string;
    address: string;
    orders: DeliveryTaskOrderType[];
}

interface BatchFailedTemporaryProps {
}

// 根据异常原因获取拍照配置
const getExceptionPhotoConfig = (exceptionReasonCode: string) => {
    const code = exceptionReasonCode || '';

    // 地址异常
    if (code === "ERDL001") {
        return {
            minPhotos: 1,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.addressException.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.addressException.cameraPagePrompt')
        };
    }
    // 无法进入派件区域
    if (code === "ERDL002") {
        return {
            minPhotos: 1,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.noAccess.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.noAccess.cameraPagePrompt')
        };
    }
    // 商户未营业
    if (code === "ERDL003") {
        return {
            minPhotos: 2,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.notInBusiness.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.notInBusiness.cameraPagePrompt')
        };
    }
    // 收件人拒收 defaultPhotoRequirement
    if (code === "ERDL004") {
        return {
            minPhotos: 1,
            requirementText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.refusedFreight.detailPagePrompt'),
            cameraText: t('pages.delivery.temporary.batchFailed.exceptionPhoto.refusedFreight.cameraPagePrompt')
        };
    }

    // 默认配置
    return {
        minPhotos: 1,
        requirementText: t('pages.delivery.temporary.batchFailed.defaultPhotoRequirement'),
        cameraText: t('pages.delivery.temporary.batchFailed.defaultCameraText')
    };
};

const BatchFailedTemporaryPage: React.FC<BatchFailedTemporaryProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const deliveryTaskOrderIdList = location.state?.deliveryTaskOrderIdList || [];
    const exceptionReason: ExceptionDictionaryType = location.state?.exceptionReason;
    const exceptionSubReasons: ExceptionSubReasonType[] = location.state?.exceptionSubReasons ?? (location.state?.exceptionSubReason ? [location.state.exceptionSubReason] : []);
    const {deliveryTaskOrders, loading} = useDeliveryTaskOrders(deliveryTaskOrderIdList);
    const fromNativeMapMode = location.state?.fromNativeMapMode || false;
    const {position} = useLocationStore();
    const {handleDeliveryFailed} = useDeliveryTaskOrderOperations();
    const {isOnline} = useNetwork();
    const fromMapModeFromState = Boolean(location.state?.fromMapMode);

    // ✅ 从 Store 获取图片
    const { getTempScanPhotos, clearTempScanPhotos } = useTempScanPhotos();

    // 订单及客户信息展开状态（同时控制包裹列表的显示数量）
    const [isCustomerInfoExpanded, setIsCustomerInfoExpanded] = useState(false);
    // 每个订单项的展开状态
    const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

    // 照片相关状态
    const [selectedPhotoType, setSelectedPhotoType] = useState<'label' | 'location'>('label');
    const [photosArray, setPhotosArray] = useState<PhotoType[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showPackageSelectDialog, setShowPackageSelectDialog] = useState<boolean>(false);
    const [selectedPackageForPhoto, setSelectedPackageForPhoto] = useState<string>('');
    const [packageSearchText, setPackageSearchText] = useState<string>('');
    const [pendingPhotoAction, setPendingPhotoAction] = useState<'camera' | 'gallery' | null>(null);
    const [labelPhotosMap, setLabelPhotosMap] = useState<Map<string, string>>(new Map());
    const hasAutoOpenedCamera = useRef<boolean>(false);

    // 新增：优化图片状态
    const [viewingOptimizedPhoto, setViewingOptimizedPhoto] = useState<OptimizedPhoto | null>(null);

    // 获取异常原因的拍照配置
    const photoConfig = getExceptionPhotoConfig(exceptionReason?.exceptionReasonCode || '');

    // 新增：优化 label 图片
    const labelPhotosArray = useMemo(() => Array.from(labelPhotosMap.values()), [labelPhotosMap]);
    const labelTrackingNumbers = useMemo(() => Array.from(labelPhotosMap.keys()), [labelPhotosMap]);
    const optimizedLabelPhotos = useOptimizedPhotos(labelPhotosArray, labelTrackingNumbers, {
        enableThumbnail: true,
        cacheSize: 10
    });

    // 新增：优化 location 图片
    const locationPhotosArray = useMemo(() => photosArray.map(p => p.url), [photosArray]);
    const optimizedLocationPhotos = useOptimizedPhotos(locationPhotosArray, undefined, {
        enableThumbnail: true,
        cacheSize: 10
    });

    sessionStorage.setItem('returningFromDetail', 'true');

    // 检查是否来自原生地图模式
    const isFromNativeMapMode = fromNativeMapMode;

    // 请求权限
    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);


    // 初始化包裹面单照片映射（从 deliveryStore 读取）
    useEffect(() => {
        // ✅ 从 Store 读取图片
        const storePhotos = getTempScanPhotos();

        // 使用 Store 中的图片
        const initialMap = new Map<string, string>();

        if (storePhotos.size > 0) {
            // 从 Store 读取
            storePhotos.forEach((base64, trackingNumber) => {
                initialMap.set(trackingNumber, base64);
            });
        }
        setLabelPhotosMap(initialMap);

        // 组件卸载时清理（防止内存泄漏）
        return () => {
            // 如果用户直接返回（未完成操作），也清空图片
            clearTempScanPhotos();
        };
    }, []);

    // 从扫描页跳转过来时，自动打开拍照
        // 从扫描页跳转过来时，自动打开拍照
        useEffect(() => {
            // 判断是否有scanResults，表示从扫描页面跳转过来，且还未自动打开过
            if (!hasAutoOpenedCamera.current && deliveryTaskOrders.length > 0) {
                hasAutoOpenedCamera.current = true;
    
                if (isOnline) {
                    // 有网络，打开位置及门牌拍照
                    setSelectedPhotoType('location');
                    handleMultiPhotoGalleryBurstCapture();
                } else {
                    // 无网络，打开包裹面单拍照页面
                    setSelectedPhotoType('label');
                    handleOpenTrackNumCamera();
                }
            }
        }, [deliveryTaskOrders.length, isOnline]);

    // 按收件人+手机号分组订单
    const groupOrdersByRecipient = (): OrderGroup[] => {
        const groupMap = new Map<string, OrderGroup>();

        deliveryTaskOrders.forEach(order => {
            const fullName = order.orderWithAddress?.deliveryAddress?.fullName || '';
            const phone = order.orderWithAddress?.deliveryAddress?.phoneNumber || '';
            const key = `${fullName}_${phone}`;

            if (!groupMap.has(key)) {
                const address = order.orderWithAddress?.deliveryAddress?.standardizedAddressString || [
                    order.orderWithAddress?.deliveryAddress?.addressLine1 || '',
                    order.orderWithAddress?.deliveryAddress?.addressLine2 || '',
                    order.orderWithAddress?.deliveryAddress?.city || '',
                    order.orderWithAddress?.deliveryAddress?.state || '',
                    order.orderWithAddress?.deliveryAddress?.zipcode || '',
                    'US'
                ].filter(Boolean).join(' ');

                groupMap.set(key, {
                    fullName,
                    phone,
                    address,
                    orders: []
                });
            }

            groupMap.get(key)!.orders.push(order);
        });

        return Array.from(groupMap.values());
    };

    const orderGroups = useMemo(() => groupOrdersByRecipient(), [deliveryTaskOrders]);

    // 切换订单项展开状态
    const toggleOrderExpanded = (orderId: number) => {
        setExpandedOrders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(orderId)) {
                newSet.delete(orderId);
            } else {
                newSet.add(orderId);
            }
            return newSet;
        });
    };

    /*照片相关操作 start======*/

    // 删除照片（数组）
    const deletePhotoArray = (index: number) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                multiPhotoService.handleDeletePhotoArray(index, (updateFn) => {
                    setPhotosArray((prev) => updateFn(prev));
                });
            },
        });
    };

    // 删除包裹面单照片
    const deleteLabelPhoto = (trackingNumber: string) => {
        Dialog.confirm({
            content: (
                <div className="text-left">
                    <div className="mb-2 text-center font-bold">
                        {t('pages.delivery.temporary.common.deleteLabelPhotoTitle')}
                    </div>
                    <div className="text-sm">
                        {t('pages.delivery.temporary.common.deleteLabelPhotoPrefix')}
                        <span className="text-red-500">{t('pages.delivery.temporary.common.deleteLabelPhotoHighlight')}</span>
                        {t('pages.delivery.temporary.common.deleteLabelPhotoSuffix')}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                        {t('pages.delivery.temporary.common.deleteLabelPhotoNote')}
                    </div>
                </div>
            ),
            confirmText: t('pages.delivery.temporary.common.retakePhoto'),
            cancelText: t('pages.delivery.temporary.common.deletePhotoCancel'),
            onConfirm: () => {
                // 点击"重新拍摄"：先删除照片；仅当删除后只剩一单未拍照时直接进入拍照，否则打开包裹选择弹窗
                setLabelPhotosMap(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(trackingNumber);
                    return newMap;
                });
                const missingAfterDelete = allTrackingNumbers.length - (labelPhotosMap.size - 1);
                if (missingAfterDelete === 1) {
                    openCameraForPackage(trackingNumber);
                } else {
                    setSelectedPackageForPhoto(trackingNumber);
                    setPendingPhotoAction('camera');
                    setShowPackageSelectDialog(true);
                }
            },
            onCancel: () => {
            },
        });
    };

    // 仅缺一面单时直接进入拍照（不打开包裹选择弹窗）
    const openCameraForPackage = (trackingNumber: string) => {
        const photoTips = t('pages.delivery.temporary.common.takeLabelPhoto') || '';
        const trackNumBeansJson = JSON.stringify([{ trackNum: trackingNumber }]);
        openTrackNumCamera(1, 1, photoTips, '', trackNumBeansJson, async (items) => {
            if (items.length > 0) {
                const photoItem = items.find(item => item.trackNum === trackingNumber);
                if (photoItem && photoItem.photoBase64) {
                    setLabelPhotosMap(prev => {
                        const newMap = new Map(prev);
                        newMap.set(trackingNumber, photoItem.photoBase64);
                        return newMap;
                    });
                }
            }
            setSelectedPackageForPhoto('');
            setPendingPhotoAction(null);
        });
    };

    // 仅缺一面单时直接进入相册选择（不打开包裹选择弹窗）
    const openGalleryForPackage = (trackingNumber: string) => {
        openPhotoPicker(1, 1, t('pages.delivery.temporary.common.takeLabelPhoto') || '', (photos: string[]) => {
            if (photos.length > 0) {
                setLabelPhotosMap(prev => {
                    const newMap = new Map(prev);
                    newMap.set(trackingNumber, photos[0]);
                    return newMap;
                });
            }
            setSelectedPackageForPhoto('');
            setPendingPhotoAction(null);
        });
    };

    // 打开包裹选择弹窗（拍照）；仅缺一面单时直接进入拍照
    const handleOpenCameraForLabel = () => {
        const missing = allTrackingNumbers.filter(tn => !labelPhotosMap.has(tn));
        if (missing.length === 1) {
            openCameraForPackage(missing[0]);
            return;
        }
        setSelectedPackageForPhoto('');
        setPendingPhotoAction('camera');
        setShowPackageSelectDialog(true);
    };

    // 打开包裹选择弹窗（相册）；仅缺一面单时直接进入相册选择
    const handleOpenGalleryForLabel = () => {
        const missing = allTrackingNumbers.filter(tn => !labelPhotosMap.has(tn));
        if (missing.length === 1) {
            openGalleryForPackage(missing[0]);
            return;
        }
        setSelectedPackageForPhoto('');
        setPendingPhotoAction('gallery');
        setShowPackageSelectDialog(true);
    };

        // 只适用于无网首次进入页面拍照场景
        const handleOpenTrackNumCamera = () => {
            const photoTips = t('pages.delivery.temporary.common.takeLabelPhoto') || '';
            
            // 准备所有包裹的运单号列表
            const trackNums = deliveryTaskOrders.map(order => ({
                trackNum: order.trackingNumber
            }));
            const trackNumBeansJson = JSON.stringify(trackNums);
            const maxPhotoCount = trackNums.length;
            
            openTrackNumCamera(maxPhotoCount, maxPhotoCount, photoTips, '', trackNumBeansJson, async (items) => {
                if (items.length > 0) {
                    // 新的返回结构: { trackNum: string; photoBase64: string }[]
                    setLabelPhotosMap(prev => {
                        const newMap = new Map(prev);
                        items.forEach(item => {
                            if (item.trackNum && item.photoBase64) {
                                newMap.set(item.trackNum, item.photoBase64);
                            }
                        });
                        return newMap;
                    });
                }
            });
        };

    // 确认选择包裹并拍照/选择照片
    const handleConfirmPackageSelection = async () => {
        if (!selectedPackageForPhoto) {
            return;
        }

        const packageToUse = selectedPackageForPhoto;
        setShowPackageSelectDialog(false);
        setPackageSearchText('');
        setSelectedPackageForPhoto('');
        setPendingPhotoAction(null);

        if (pendingPhotoAction === 'camera') {
            openCameraForPackage(packageToUse);
        } else if (pendingPhotoAction === 'gallery') {
            openGalleryForPackage(packageToUse);
        }
    };

    // 处理多图画廊的连拍功能（新版本数组形式 - 位置及门牌）
    const handleMultiPhotoGalleryBurstCapture = () => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);

        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= photoConfig.minPhotos ? 0 : photoConfig.minPhotos - currentPhotoCount;

        openCamera(remainingPhotoCount, minSelectionCount, photoConfig.cameraText, async (photos: string[]) => {
            try {
                const photosWithPosition: Array<{ photo: string; position: GeoPosition | null }> = [];

                for (const photo of photos) {
                    let currentPosition: GeoPosition | null = {
                        latitude: 0,
                        longitude: 0,
                        accuracy: 0,
                        timestamp: 0,
                    };
                    photosWithPosition.push({photo, position: currentPosition});
                }

                await multiPhotoService.handleMultiPhotoSelectionArrayWithIndividualPositionsBatched(
                    photosWithPosition,
                    (updateFn) => setPhotosArray(updateFn),
                    'camera'
                );
            } catch (error) {
                console.error('多图片处理失败:', error);
                Toast.show({
                    content: t('pages.delivery.temporary.common.multiPhotoProcessFailed'),
                    position: 'center'
                });
            }
        });
    };

    /*照片相关操作 end======*/

    // 获取所有包裹号列表
    const allTrackingNumbers = deliveryTaskOrders.map(order =>
        order.orderWithAddress?.order?.trackingNumber || ''
    ).filter(Boolean);

    // 处理开始派送
    const handleStartDeliveryVerification = () => {
        // 防止重复点击
        if (isLoading) {
            return;
        }

        // 1. 校验位置及门牌图片数量
        if (photosArray.length < photoConfig.minPhotos) {
            Toast.show({
                content: t('pages.delivery.temporary.common.minLocationPhotosRequired', {count: photoConfig.minPhotos}),
                position: 'center'
            });
            return;
        }

        // 2. 校验包裹面单数量是否匹配
        const totalPackages = allTrackingNumbers.length;
        const scannedPackages = labelPhotosMap.size;

        if (scannedPackages < totalPackages) {
            Dialog.confirm({
                content: (
                    <div className="text-left">
                        <div className="mb-2 text-center font-bold">
                            {t('pages.delivery.temporary.common.missingLabelPhotosTitle')}
                        </div>
                        <p className="mb-2">
                            {t('pages.delivery.temporary.common.missingLabelPhotosPrefix', {count: totalPackages - scannedPackages})}
                            <span className="text-red-500">{t('pages.delivery.temporary.common.missingLabelPhotosHighlight')}</span>
                            <span>{t('pages.delivery.temporary.common.missingLabelPhotosSuffix')}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                            {t('pages.delivery.temporary.common.missingLabelPhotosNote')}
                        </p>
                    </div>
                ),
                confirmText: t('pages.delivery.temporary.common.returnToTake'),
                onConfirm: () => {
                    setSelectedPhotoType('label');
                    const missingCount = totalPackages - scannedPackages;
                    if (missingCount === 1) {
                        const onlyMissing = allTrackingNumbers.find(tn => !labelPhotosMap.has(tn));
                        if (onlyMissing) {
                            openCameraForPackage(onlyMissing);
                            return;
                        }
                    }
                    handleOpenCameraForLabel();
                }
            });
            return;
        }

        // 所有校验通过，执行派送逻辑
        proceedWithException();
    };

    // 执行异常标记逻辑
    const proceedWithException = async () => {
        setIsLoading(true);

        try {
            // ✅ 获取完成时的实时经纬度（用于 location 字段）
            let completionPosition = position; // 默认使用缓存位置

            // 获取完成时的实时经纬度（强制获取最新位置，不使用缓存）
            try {
                completionPosition = await geolocationService.getFreshPosition();
                console.log('📍 完成异常标记时获取到最新位置:', completionPosition);
            } catch (error) {
                console.warn('⚠️ 获取位置失败，使用缓存位置:', error);
            }

            // 同步处理订单，等待所有订单状态更新完成后再跳转
            const now = Date.now();
            const dateString = new Date().toISOString();

            // 构建处理上下文
            const context: OrderProcessContext = {
                labelPhotosMap,
                photosArray,
                position,
                completionPosition,
                dateString,
                now,
                exceptionReasonCode: exceptionReason.exceptionReasonCode,
                exceptionSubReasons: exceptionSubReasons.length > 0 ? exceptionSubReasons.map(s => s.code) : undefined
            };

            // 使用批量处理器处理失败订单
            const results = await OrderBatchProcessor.processFailedOrders(
                deliveryTaskOrders,
                context,
                handleDeliveryFailed,
                {
                    showProgress: true,
                    workerThreshold: 100,
                    batchSize: 20
                },
                (processed: number, total: number) => {
                    // 进度回调（可选）
                    if (processed % 20 === 0 || processed === total) {
                        console.log(`📦 批量异常标记进度: ${processed}/${total}`);
                    }
                }
            );

            // 统计处理结果
            const successCount = results.filter((r: any) => r.success).length;
            const failedCount = results.length - successCount;

            console.log(`✅ 批量异常标记完成: 成功 ${successCount} 单, 失败 ${failedCount} 单`);

            if (failedCount > 0) {
                console.warn('⚠️ 部分订单处理失败:', results.filter((r: any) => !r.success));
            }

            // ✅ 异常标记完成后清空临时图片
            clearTempScanPhotos();

            // 所有订单处理完成后，根据导航来源决定返回路径
            sessionStorage.setItem('batch_flow_completed', 'true');
            if (isFromNativeMapMode) {
                // 从原生地图跳转过来，返回后需要重新打开原生地图
                openNativeMapMode();
                navigateBack.navigateToDeliveringList(navigate);
            } else if (fromMapModeFromState) {
                // 使用兼容的导航方式
                sessionStorage.setItem('returningFromDetail', 'true');
                sessionStorage.setItem('pickUpSuccess', 'true');
                navigate("/delivery/temporary/map-mode");
            } else {
                const activeTab = location.state?.activeTab;
                navigate("/delivery/delivering/temporary", { state: { activeTab } });
            }

        } catch (error: any) {
            console.error('批量异常标记失败:', error);
            Toast.show({content: t('pages.delivery.temporary.batchFailed.exceptionFailed'), position: 'center'});
        } finally {
            setIsLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => {
                        if (isFromNativeMapMode) {
                            openNativeMapMode();
                            navigateBack.navigateToDeliveringList(navigate);
                        } else if (fromMapModeFromState) {
                            navigate("/delivery/temporary/map-mode");
                        } else {
                            // 返回到 OrderDetailTemporaryPage
                            const firstOrder = deliveryTaskOrders?.[0];
                            if (firstOrder?.trackingNumber) {
                                navigate(`/delivery/delivering/temporary/${firstOrder.trackingNumber}`, {
                                    state: {
                                        deliveryTaskOrderId: firstOrder.id,
                                        trackingNumber: firstOrder.trackingNumber,
                                        activeTab: location.state?.activeTab,
                                        fromMapMode: fromMapModeFromState
                                    }
                                });
                            } else {
                                const activeTab = location.state?.activeTab;
                                navigate("/delivery/delivering/temporary", { state: { activeTab } });
                            }
                        }
                    }}>
                        {t('pages.delivery.temporary.batchFailed.title')}
                    </NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
            </div>
        );
    }

    if (!deliveryTaskOrders || deliveryTaskOrders.length === 0) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => {
                        if (isFromNativeMapMode) {
                            openNativeMapMode()
                            navigateBack.navigateToDeliveringList(navigate);
                        } else if (fromMapModeFromState) {
                            navigate("/delivery/temporary/map-mode");
                        } else {
                            // 返回到 OrderDetailTemporaryPage
                            const firstOrder = deliveryTaskOrders?.[0];
                            if (firstOrder?.trackingNumber) {
                                navigate(`/delivery/delivering/temporary/${firstOrder.trackingNumber}`, {
                                    state: {
                                        deliveryTaskOrderId: firstOrder.id,
                                        trackingNumber: firstOrder.trackingNumber,
                                        activeTab: location.state?.activeTab,
                                        fromMapMode: fromMapModeFromState
                                    }
                                });
                            } else {
                                const activeTab = location.state?.activeTab;
                                navigate("/delivery/delivering/temporary", { state: { activeTab } });
                            }
                        }
                    }}>
                        {t('pages.delivery.temporary.batchFailed.title')}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    // 获取第一个订单组的地址用于顶部显示
    const firstAddress = orderGroups.length > 0 ? orderGroups[0].address : '';

    return (
        <div className="flex flex-col h-screen ">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => {
                    if (isFromNativeMapMode) {
                        openNativeMapMode()
                        navigateBack.navigateToDeliveringList(navigate);
                    } else if (fromMapModeFromState) {
                        navigate("/delivery/temporary/map-mode");
                    } else {
                        // 返回到 OrderDetailTemporaryPage
                        const firstOrder = deliveryTaskOrders?.[0];
                        if (firstOrder?.trackingNumber) {
                            navigate(`/delivery/delivering/temporary/${firstOrder.trackingNumber}`, {
                                state: {
                                    deliveryTaskOrderId: firstOrder.id,
                                    trackingNumber: firstOrder.trackingNumber,
                                    activeTab: location.state?.activeTab,
                                    fromMapMode: fromMapModeFromState
                                }
                            });
                        } else {
                            const activeTab = location.state?.activeTab;
                            navigate("/delivery/delivering/temporary", { state: { activeTab } });
                        }
                    }
                }}>
                    {t('pages.delivery.temporary.batchFailed.title')}
                </NavBar>
            </div>

            {/* 标题栏及展开收起button - 固定在顶部 */}
            <div className="bg-white px-4 py-2 flex items-center justify-between border-b">
                <h2 className="text-base font-semibold">{t('pages.delivery.temporary.common.orderAndCustomerInfo')}</h2>
                <div
                    className="flex items-center cursor-pointer"
                    onClick={() => setIsCustomerInfoExpanded(!isCustomerInfoExpanded)}
                >
                    <span className="text-sm text-gray-600 mr-1">
                        {isCustomerInfoExpanded ? t('pages.delivery.temporary.common.collapse') : t('pages.delivery.temporary.common.expand')}
                    </span>
                    {isCustomerInfoExpanded ? <UpOutline/> : <DownOutline/>}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 地址+包裹列表 */}
                <div className="px-4 py-2 space-y-3 border-b">

                    <div className="bg-white px-4 py-3 border-1 rounded-lg">
                        <p className="text-sm font-bold">{firstAddress}</p>
                    </div>

                    {/* 包裹列表容器 - 使用优化的虚拟滚动组件 */}
                    <VirtualOrderList
                        orderGroups={orderGroups}
                        isExpanded={isCustomerInfoExpanded}
                        expandedOrders={expandedOrders}
                        exceptionReason={exceptionReason}
                        exceptionSubReasons={exceptionSubReasons}
                        onToggleOrder={toggleOrderExpanded}
                    />
                </div>

                {/* Photos 照片组件 */}
                <div className="px-4 py-4 bg-white">
                    <h2 className="text-base font-semibold mb-2">{t('pages.delivery.temporary.common.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-2">
                        {photoConfig.requirementText}<br/>
                        {labelPhotosMap.size === 0 && (
                            <>
                                <span
                                    className="text-red-500">{t('pages.delivery.temporary.common.noLabelScreenshot')}</span>
                            </>
                        )}
                        {labelPhotosMap.size > 0 && (
                            <>
                                <span
                                    className="text-red-500">{t('pages.delivery.temporary.common.labelPhotosAreScreenshots')}</span>
                            </>
                        )}
                    </p>

                    {/* 照片类型卡片 */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        {/* 包裹面单卡片 */}
                        <div
                            className={`relative rounded-lg overflow-hidden cursor-pointer transition-all ring-1 ring-gray-200`}
                            onClick={() => setSelectedPhotoType('label')}
                        >
                            {/* 缩略图 */}
                            <div className="aspect-square bg-gray-100">
                                {Array.from(labelPhotosMap.values())[0] ? (
                                    <img
                                        src={Array.from(labelPhotosMap.values())[0]}
                                        alt={t('pages.delivery.temporary.common.labelPhoto')}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor"
                                             viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                            {/* 底部文字和箭头 */}
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white py-1">
                                <div className="flex flex-col items-center">
                                    <span
                                        className="text-xs font-medium text-center">{t('pages.delivery.temporary.common.labelPhoto')} ({labelPhotosMap.size})</span>
                                    {selectedPhotoType === 'label' ? (
                                        <UpOutline className="text-xs"/>
                                    ) : (
                                        <DownOutline className="text-xs"/>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 位置及门牌卡片 */}
                        <div
                            className={`relative rounded-lg overflow-hidden cursor-pointer transition-all ring-1 ring-gray-200`}
                            onClick={() => setSelectedPhotoType('location')}
                        >
                            {/* 缩略图 */}
                            <div className="aspect-square bg-gray-100">
                                {photosArray[0]?.url ? (
                                    <img
                                        src={photosArray[0].url}
                                        alt={t('pages.delivery.temporary.common.locationPhoto')}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor"
                                             viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                            {/* 底部文字和箭头 */}
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white py-1">
                                <div className="flex flex-col items-center">
                                    <span
                                        className="text-xs font-medium text-center">{t('pages.delivery.temporary.common.locationPhoto')} ({photosArray.length})</span>
                                    {selectedPhotoType === 'location' ? (
                                        <UpOutline className="text-xs"/>
                                    ) : (
                                        <DownOutline className="text-xs"/>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 包裹面单照片展示 */}
                    {selectedPhotoType === 'label' && (
                        <>
                            <VirtualPhotoList
                                photos={optimizedLabelPhotos.photos}
                                onPhotoClick={(photo) => setViewingOptimizedPhoto(photo)}
                                onPhotoDelete={(photo) => {
                                    // 从 Map 中删除
                                    if (photo.trackingNumber) {
                                        deleteLabelPhoto(photo.trackingNumber);
                                    }
                                }}
                                onAddPhoto={handleOpenCameraForLabel}
                                showAddButton={allTrackingNumbers.length > labelPhotosMap.size}
                            />

                            {/* 相册选择器 */}
                            {allTrackingNumbers.length > labelPhotosMap.size && (
                                <div className="mt-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[13px] text-gray-700">
                                            {t('pages.delivery.detail.cantTakePhotos')}
                                        </span>
                                        <div className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md bg-white cursor-pointer" onClick={handleOpenGalleryForLabel}>
                                            <span className="text-[13px] text-gray-700 font-medium">
                                                {t('pages.delivery.detail.uploadFromAlbum')}
                                            </span>
                                            <CloudUploadOutlined className="text-gray-500 text-lg" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* 位置及门牌照片展示 */}
                    {selectedPhotoType === 'location' && (
                        <>
                            <VirtualPhotoList
                                photos={optimizedLocationPhotos.photos}
                                onPhotoClick={(photo) => setViewingOptimizedPhoto(photo)}
                                onPhotoDelete={(photo) => {
                                    // 找到原始索引并删除
                                    const index = optimizedLocationPhotos.photos.findIndex(p => p.id === photo.id);
                                    if (index !== -1) {
                                        deletePhotoArray(index);
                                    }
                                }}
                                onAddPhoto={handleMultiPhotoGalleryBurstCapture}
                                showAddButton={photosArray.length < kMaxSelectorPhotoLength}
                            />

                            {/* 相册上传 */}
                            {photosArray.length < kMaxSelectorPhotoLength && (
                                <div className="mt-4">
                                    <MultiPhotoSelectorView
                                        minPhotos={photosArray.length >= photoConfig.minPhotos ? 0 : photoConfig.minPhotos - photosArray.length}
                                        maxPhotos={Math.max(0, kMaxSelectorPhotoLength - photosArray.length)}
                                        onPhotoSelected={async (photos: string[]) => {
                                            try {
                                                let currentPosition: GeoPosition | null = {
                                                    latitude: 0,
                                                    longitude: 0,
                                                    accuracy: 0,
                                                    timestamp: 0
                                                };

                                                const result = await multiPhotoService.handleMultiPhotoSelectionArrayWithBatched(
                                                    photos,
                                                    (updateFn) => setPhotosArray(updateFn),
                                                    currentPosition,
                                                    'gallery'
                                                );

                                                if (!result.success) {
                                                    console.warn('部分图片上传失败:', result.errors);
                                                }
                                            } catch (error) {
                                                console.error('多图片处理失败:', error);
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* 查看大图 */}
                <OptimizedPhotoViewer
                    photo={viewingOptimizedPhoto}
                    onClose={() => setViewingOptimizedPhoto(null)}
                    getFullImageUrl={selectedPhotoType === 'label'
                        ? optimizedLabelPhotos.getFullImageUrl
                        : optimizedLocationPhotos.getFullImageUrl
                    }
                />

                {/* 包裹选择弹窗 */}
                <Popup
                    visible={showPackageSelectDialog}
                    onMaskClick={() => {
                        setShowPackageSelectDialog(false);
                        setPackageSearchText('');
                    }}
                    bodyStyle={{
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        height: '70vh',
                        padding: '0'
                    }}
                >
                    <div className="flex flex-col h-full p-4">
                        <h3 className="text-lg font-semibold mb-4 text-center">{t('pages.delivery.temporary.common.selectPackageToTake')}</h3>

                        {/* 搜索框 */}
                        <div className="mb-4 flex-shrink-0">
                            <style>
                                {`
                                    .custom-search-input input::placeholder {
                                        font-size: 14px !important;
                                    }
                                `}
                            </style>
                            <Input
                                className="custom-search-input"
                                placeholder={t('pages.delivery.temporary.common.enterTrackingNumberLast6')}
                                clearable
                                value={packageSearchText}
                                onChange={(val) => setPackageSearchText(val)}
                            />
                        </div>

                        {/* 包裹列表 */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {allTrackingNumbers
                                .filter(tn => !labelPhotosMap.has(tn)) // 过滤掉已有照片的包裹
                                .filter(tn =>
                                    !packageSearchText ||
                                    tn.toLowerCase().includes(packageSearchText.toLowerCase())
                                )
                                .map((trackingNumber) => (
                                    <div
                                        key={trackingNumber}
                                        className={`flex items-center justify-between p-3 mb-2 border rounded-lg cursor-pointer ${
                                            selectedPackageForPhoto === trackingNumber
                                                ? 'border-purple-500'
                                                : 'border-gray-200'
                                        }`}
                                        onClick={() => setSelectedPackageForPhoto(trackingNumber)}
                                    >
                                        <span className="text-sm">{trackingNumber}</span>
                                        {selectedPackageForPhoto === trackingNumber && (
                                            <div
                                                className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                                                <span className="text-white text-xs">✓</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>

                        {/* 确认按钮 */}
                        <div className="mt-4 pt-4 border-t border-gray-200 flex-shrink-0">
                            <Button
                                block
                                color="primary"
                                onClick={handleConfirmPackageSelection}
                                disabled={!selectedPackageForPhoto}
                            >
                                {t('pages.delivery.temporary.common.confirm')}
                            </Button>
                        </div>
                    </div>
                </Popup>

            </div>

            {/* 完成按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    onClick={handleStartDeliveryVerification}
                    loading={isLoading}
                    disabled={photosArray.length < photoConfig.minPhotos}
                >
                    {t('pages.delivery.detail.complete')}
                </Button>
            </div>
        </div>
    );
};

export default BatchFailedTemporaryPage;
